import { useRef, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideHover } from "./useTourGuideHover";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";

/**
 * Tour Guide Hook for Unauthenticated Courses Page
 *
 * Guides users through the courses browsing features:
 * - Search functionality
 * - Category filters
 * - Requirement filters
 * - Course cards and enrollment
 */
export const useUnauthenticatedCoursesTourGuide = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();

  // Create refs for elements that need them
  const courseCardRef = useRef<HTMLDivElement>(null);

  // Detect if we're on the unauthenticated courses route
  const isOnUnauthenticatedCourses =
    pathname === ROUTE_NAMES.UNAUTHENTICATED_COURSES;

  // Define tour configuration
  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "unauthenticatedCourses",
      enabled: isOnUnauthenticatedCourses,
      steps: [
        // Search Bar Step
        {
          id: "courses-search",
          targetSelector: '[data-tour-item="courses-search"]',
          title: t("tourGuide.unauthenticatedCourses.step1.title"),
          description: t("tourGuide.unauthenticatedCourses.step1.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 8,
        },
        // Category Filter Step
        {
          id: "courses-category-filter",
          targetSelector: '[data-tour-item="courses-category-filter"]',
          title: t("tourGuide.unauthenticatedCourses.step2.title"),
          description: t("tourGuide.unauthenticatedCourses.step2.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 8,
        },
        // Requirement Filter Step
        {
          id: "courses-requirement-filter",
          targetSelector: '[data-tour-item="courses-requirement-filter"]',
          title: t("tourGuide.unauthenticatedCourses.step3.title"),
          description: t("tourGuide.unauthenticatedCourses.step3.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 8,
        },
        // Course Card Step
        {
          id: "course-card",
          targetRef: courseCardRef,
          title: t("tourGuide.unauthenticatedCourses.step4.title"),
          description: t("tourGuide.unauthenticatedCourses.step4.description"),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 12,
        },
      ],
    }),
    [isOnUnauthenticatedCourses, t],
  );

  // Create base tour guide
  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            { type: TourGuideType.UnauthenticatedCourses, isDone: true },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const unauthenticatedCoursesTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.UnauthenticatedCourses,
    isOnTourPath: isOnUnauthenticatedCourses,
    onTourComplete: handleTourComplete,
  });

  // Sync with tour guide context
  useEffect(() => {
    if (unauthenticatedCoursesTourGuide.isActive) {
      setActiveTourModule("unauthenticatedCourses");
      setCurrentTourStep(unauthenticatedCoursesTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    unauthenticatedCoursesTourGuide.isActive,
    unauthenticatedCoursesTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  // Create hover tooltip instances for each step
  const searchHover = useTourGuideHover("courses-search", config.steps[0]);
  const categoryFilterHover = useTourGuideHover(
    "courses-category-filter",
    config.steps[1],
  );
  const requirementFilterHover = useTourGuideHover(
    "courses-requirement-filter",
    config.steps[2],
  );
  const courseCardHover = useTourGuideHover("course-card", config.steps[3]);

  return {
    unauthenticatedCoursesTourGuide,
    // Refs to attach to elements
    courseCardRef,
    // Hover tooltip instances
    searchHover,
    categoryFilterHover,
    requirementFilterHover,
    courseCardHover,
  };
};
